import React, { useState, useEffect, useRef } from 'react';
import { Activity, ArrowUp, ArrowDown, ChevronDown, ChevronUp } from 'lucide-react';

export const EventImpactPanel = ({ events, moodColor }) => {
  const [displayEvents, setDisplayEvents] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    if (events?.length) {
      setDisplayEvents(events);
      hasLoadedOnce.current = true;
    }
  }, [events]);

  const allEvents = [...(displayEvents || [])];
  
  if (!allEvents?.length) {
    if (!hasLoadedOnce.current) {
      return (
        <div className="empty-state">
          <Activity size={20} style={{ opacity: 0.5 }} />
          <span>Loading events...</span>
        </div>
      );
    }
  }

  const positiveEvents = allEvents
    .filter(e => e.sentiment === 'positive')
    .sort((a, b) => (b.impact || 0) - (a.impact || 0))
    .slice(0, 4);
  
  const negativeEvents = allEvents
    .filter(e => e.sentiment === 'negative')
    .sort((a, b) => (b.impact || 0) - (a.impact || 0))
    .slice(0, 4);

  if (positiveEvents.length < 2) {
    const neutralEvents = allEvents
      .filter(e => e.sentiment === 'neutral' || !e.sentiment)
      .slice(0, 2 - positiveEvents.length);
    positiveEvents.push(...neutralEvents.map(e => ({ ...e, sentiment: 'positive' })));
  }

  const renderEvent = (event, idx, isPositive, globalIdx) => {
    const isExpanded = expandedIdx === globalIdx;
    const impactPct = Math.round((event.impact || 0.5) * 100);
    
    return (
      <div 
        key={idx} 
        className={`event-row ${isPositive ? 'positive' : 'negative'} ${isExpanded ? 'expanded' : ''}`}
        data-testid={`event-item-${globalIdx}`}
        onClick={() => setExpandedIdx(isExpanded ? null : globalIdx)}
        style={{ cursor: 'pointer' }}
      >
        <div className="event-row-main">
          <span className="event-icon">{isPositive ? '▲' : '▼'}</span>
          <span className="event-label">{event.event}</span>
          <span className="event-expand-icon">
            {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </span>
        </div>
        {isExpanded && (
          <div className="event-details" data-testid={`event-details-${globalIdx}`}>
            <div className="event-impact-bar">
              <span className="event-detail-label">Impact</span>
              <div className="event-bar-track">
                <div 
                  className={`event-bar-fill ${isPositive ? 'positive' : 'negative'}`}
                  style={{ width: `${impactPct}%` }}
                />
              </div>
              <span className="event-detail-value">{impactPct}%</span>
            </div>
            <div className="event-sentiment-tag">
              <span className={`sentiment-badge ${event.sentiment || 'neutral'}`}>
                {(event.sentiment || 'neutral').toUpperCase()}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="event-impact-panel" data-testid="event-impact">
      <div className="event-col">
        <div className="event-header positive">
          <ArrowUp size={10} />
          <span>Positive</span>
        </div>
        <div className="event-list">
          {positiveEvents.length > 0 ? (
            positiveEvents.map((event, idx) => renderEvent(event, idx, true, `pos-${idx}`))
          ) : (
            <div className="event-empty">None reported</div>
          )}
        </div>
      </div>

      <div className="event-col">
        <div className="event-header negative">
          <ArrowDown size={10} />
          <span>Negative</span>
        </div>
        <div className="event-list">
          {negativeEvents.length > 0 ? (
            negativeEvents.map((event, idx) => renderEvent(event, idx, false, `neg-${idx}`))
          ) : (
            <div className="event-empty">None reported</div>
          )}
        </div>
      </div>
    </div>
  );
};
